import React from "react";
import styled, { keyframes } from "styled-components";
// Icons
import { FaTrophy, FaFileAlt } from "react-icons/fa";

// Animated background component
const AnimatedBackground = () => (
  <BackgroundContainer>
    <FloatingOrb delay="0s" size="80px" />
    <FloatingOrb delay="2s" size="120px" />
    <FloatingOrb delay="4s" size="60px" />
    <FloatingOrb delay="1s" size="90px" />
    <FloatingOrb delay="3s" size="70px" />
    <FloatingOrb delay="5s" size="110px" />
    <FloatingOrb delay="6s" size="50px" />
    <FloatingOrb delay="7s" size="95px" />
    <WavePattern />
  </BackgroundContainer>
);

export default function About() {
  return (
    <Wrapper id="about">
      <div className="whiteBg" style={{ padding: "100px 0", position: "relative", overflow: "hidden" }}>
        <AnimatedBackground />
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <HeaderInfo>
            <h1 className="font40 extraBold">About Anton Tech</h1>
          </HeaderInfo>
          
          <TwoColumnLayout>
            <LeftColumn>
              <MainDescription className="font14">
                Anton Tech is an award-winning African tech company providing enterprise-grade intelligence solutions to businesses across the continent. Our smart technology platform combines real-time analytics, compliance tools, regulatory verification, and business intelligence to drive data-driven decision-making across 8+ sectors including finance, insurance, healthcare, pharmaceutical, manufacturing, and construction.
              </MainDescription>
              
              <FlagshipProject>
                <h3 className="font24 extraBold">Five Core Pillars</h3>
                <p className="font14">
                  Our integrated platform delivers solutions across four core areas: ESG & Sustainability Solutions for carbon analytics and environmental KPI monitoring; Precision Agriculture Intelligence for specialized agricultural applications; Compliance & Digital Verification for KYC and fraud prevention; and Business Intelligence & Project Management with advanced analytics and multilingual AI support.
                </p>
              </FlagshipProject>
            </LeftColumn>
            
            <RightColumn>
              <AchievementsSection>
                <h3 className="font24 extraBold">Awards & Recognition</h3>
                <AchievementsList>
                    <AchievementItem>
                    <AwardIcon><FaTrophy /></AwardIcon> <strong>Most Innovative AgriTech Solutions Provider 2025 – Botswana</strong> - 8th Annual African Excellence Awards by MEA Markets
                  </AchievementItem>
                  <AchievementItem>
                    <AwardIcon><FaTrophy /></AwardIcon> <strong>Best Agriculture Solutions Service 2024</strong> - Africa Excellence Awards 
                  </AchievementItem>
                  <AchievementItem>
                    <AwardIcon><FaTrophy /></AwardIcon> <strong>AgriTech Startup of The Year</strong> - Southern Africa Startup Awards
                  </AchievementItem>
                  <AchievementItem>
                    <AwardIcon><FaTrophy /></AwardIcon> <strong>Creative Business Cup Botswana</strong> - Represented Botswana globally in Copenhagen, Denmark
                  </AchievementItem>
                  <AchievementItem>
                    <AwardIcon><FaTrophy /></AwardIcon> <strong>Southern Africa Innovation Collective (SAIC)</strong> - BoostUp Challenge Champions
                  </AchievementItem>
                  <AchievementItem>
                    <DocumentIcon><FaFileAlt /></DocumentIcon> <strong>Academic Publication</strong> - "The 9th International Conference on Agriculture" - "LOCATION-BASED IDENTIFICATION OF TRANSBOUNDARY PESTS & DISEASES USING UAVS & DEEP LEARNING"
                  </AchievementItem>
                </AchievementsList>
              </AchievementsSection>
            </RightColumn>
          </TwoColumnLayout>
        </div>
      </div>
    </Wrapper>
  );
}

// Animated background styles
const float = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(50px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
`;

const wave = keyframes`
  0%, 100% {
    transform: translateX(0) scaleY(1);
  }
  50% {
    transform: translateX(50px) scaleY(1.1);
  }
`;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: ${props => props.size || '100px'};
  height: ${props => props.size || '100px'};
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.12) 0%, rgba(25, 118, 210, 0.08) 100%);
  animation: ${float} 8s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  
  &:nth-child(1) {
    top: 10%;
    right: 10%;
  }
  
  &:nth-child(2) {
    bottom: 20%;
    left: 15%;
  }
  
  &:nth-child(3) {
    top: 50%;
    left: 50%;
  }
  
  &:nth-child(4) {
    top: 30%;
    right: 25%;
  }
  
  &:nth-child(5) {
    bottom: 40%;
    right: 5%;
  }
  
  &:nth-child(6) {
    top: 70%;
    left: 10%;
  }
  
  &:nth-child(7) {
    top: 15%;
    left: 30%;
  }
  
  &:nth-child(8) {
    bottom: 10%;
    left: 60%;
  }
`;

const WavePattern = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(7, 148, 135, 0.02) 25%, 
    rgba(7, 148, 135, 0.02) 75%, 
    transparent 100%
  );
  animation: ${wave} 6s ease-in-out infinite;
  clip-path: polygon(0 50%, 100% 30%, 100% 100%, 0 100%);
`;

const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
`;

const HeaderInfo = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  h1 {
    margin-bottom: 20px;
    color: #333;
  }
  
  p {
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const LeftColumn = styled.div`
  animation: fadeInLeft 0.8s ease-out 0.2s both;
  
  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const RightColumn = styled.div`
  animation: fadeInRight 0.8s ease-out 0.4s both;
  
  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const MainDescription = styled.p`
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 40px;
  color: #333;
  animation: fadeInUp 0.8s ease-out 0.2s both;
`;

const FlagshipProject = styled.div`
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.05) 0%, rgba(7, 148, 135, 0.05) 100%);
  padding: 30px;
  border-radius: 12px;
  border-left: 4px solid #079487;
  position: relative;
  overflow: hidden;
  animation: slideInLeft 0.8s ease-out 0.4s both;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    transform: rotate(45deg);
    animation: shimmerEffect 3s infinite;
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes shimmerEffect {
    0% {
      transform: translateX(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) rotate(45deg);
    }
  }
  
  h3 {
    color: #079487;
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
  }
  
  p {
    line-height: 1.7;
    color: #333;
    position: relative;
    z-index: 2;
  }
`;

const AchievementsSection = styled.div`
  animation: fadeInUp 0.8s ease-out 0.6s both;
  
  h3 {
    color: #079487;
    margin-bottom: 30px;
  }
`;

const AchievementsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const AwardIcon = styled.span`
  color: #FFD700;
  margin-right: 10px;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3));
  }
`;

const DocumentIcon = styled.span`
  color: #079487;
  margin-right: 10px;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(7, 148, 135, 0.3));
  }
`;

const AchievementItem = styled.li`
  background: white;
  padding: 20px;
  margin-bottom: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #079487;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(7, 148, 135, 0.05) 50%, transparent 100%);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 20px rgba(7, 148, 135, 0.15);
    
    &::before {
      left: 100%;
    }
  }
  
  strong {
    color: #079487;
    position: relative;
    z-index: 2;
  }
  
  /* Staggered animation for each item */
  &:nth-child(1) { animation: slideInRight 0.6s ease-out 0.8s both; }
  &:nth-child(2) { animation: slideInRight 0.6s ease-out 0.9s both; }
  &:nth-child(3) { animation: slideInRight 0.6s ease-out 1.0s both; }
  &:nth-child(4) { animation: slideInRight 0.6s ease-out 1.1s both; }
  &:nth-child(5) { animation: slideInRight 0.6s ease-out 1.2s both; }
  &:nth-child(6) { animation: slideInRight 0.6s ease-out 1.3s both; }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
